


import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Modal,
  ModalDialog,
  ModalClose,
  Select,
  Option,
  Checkbox,
  IconButton,
  Grid,
  Card,
  Chip,
  Avatar
} from '@mui/joy';
import { supabase } from '../../supabaseClient';
import StyledTable from '../../components/StyledTable';
import EnhancedButton from '../../components/EnhancedButton';
import EnhancedInput from '../../components/EnhancedInput';
import MetricCard from '../../components/MetricCard';
import LoadingSkeleton, { CardSkeleton } from '../../components/LoadingSkeleton';

const ProjectTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formState, setFormState] = useState({
    id: null,
    name: '',
    default_agency: '',
    record_type: 'Permit',
    is_required: true,
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('project_templates').select('*');
    if (!error) setTemplates(data);
    setLoading(false);
  };

  const handleSave = async () => {
    const payload = {
      name: formState.name,
      default_agency: formState.default_agency,
      record_type: formState.record_type,
      is_required: formState.is_required,
    };
    if (formState.id) {
      await supabase.from('project_templates').update(payload).eq('id', formState.id);
    } else {
      await supabase.from('project_templates').insert(payload);
    }
    setOpen(false);
    setFormState({ id: null, name: '', default_agency: '', record_type: 'Permit', is_required: true });
    fetchTemplates();
  };

  const handleDelete = async (id) => {
    await supabase.from('project_templates').delete().eq('id', id);
    fetchTemplates();
  };

  const openEdit = (template) => {
    setFormState({
      id: template.id,
      name: template.name,
      default_agency: template.default_agency,
      record_type: template.record_type,
      is_required: template.is_required,
    });
    setOpen(true);
  };

  const totalTemplates = templates.length;
  const permitTemplates = templates.filter(t => t.record_type === 'Permit').length;
  const bondTemplates = templates.filter(t => t.record_type === 'Bond').length;
  const requiredTemplates = templates.filter(t => t.is_required).length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LoadingSkeleton height="32px" width="200px" sx={{ mb: 3 }} />
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
        </Grid>
        <CardSkeleton />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Analytics */}
      <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
        <Grid xs={12} sm={6} md={3}>
          <MetricCard title="Total Templates" value={totalTemplates} icon="list_alt" gradientColors={['#00A884', '#007E33']} />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <MetricCard title="Permit Templates" value={permitTemplates} icon="description" gradientColors={['#17a2b8', '#138496']} />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <MetricCard title="Bond Templates" value={bondTemplates} icon="security" gradientColors={['#28a745', '#218838']} />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <MetricCard title="Required Templates" value={requiredTemplates} icon="priority_high" gradientColors={['#6c757d', '#5a6268']} />
        </Grid>
      </Grid>

      <Card sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)', border: '1px solid', borderColor: 'success.200' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography level="h4" sx={{ color: 'success.700', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>list_alt</span>
            Project Template Management
          </Typography>
          <EnhancedButton gradient onClick={() => setOpen(true)} startDecorator={<span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>add</span>}>
            Create Template
          </EnhancedButton>
        </Box>
      </Card>

      <StyledTable
        columns={[
          { key: 'template_info', label: 'Template', width: 300 },
          { key: 'agency', label: 'Agency', width: 200 },
          { key: 'type', label: 'Type', width: 120 },
          { key: 'required', label: 'Required', width: 100 },
          { key: 'actions', label: 'Actions', width: 120 }
        ]}
        rows={templates.map((t) => ({
          template_info: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: t.record_type === 'Permit' ? '#00A884' : '#17a2b8', color: 'white', fontSize: '0.875rem' }}>
                {t.name?.substring(0, 2)?.toUpperCase() || 'T'}
              </Avatar>
              <Typography level="title-sm" sx={{ color: 'success.700' }}>{t.name}</Typography>
            </Box>
          ),
          agency: <Typography level="body-sm">{t.default_agency || 'No Agency'}</Typography>,
          type: <Chip size="sm" variant="soft" color={t.record_type === 'Permit' ? 'success' : 'primary'}>{t.record_type}</Chip>,
          required: <Chip size="sm" variant="soft" color={t.is_required ? 'warning' : 'neutral'}>{t.is_required ? 'Yes' : 'No'}</Chip>,
          actions: (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="sm" variant="outlined" onClick={() => openEdit(t)} sx={{ '&:hover': { borderColor: 'success.500' }}}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>edit</span>
              </IconButton>
              <IconButton size="sm" color="danger" variant="outlined" onClick={() => handleDelete(t.id)}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>delete</span>
              </IconButton>
            </Box>
          )
        }))}
      />

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog sx={{ maxWidth: '500px', background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)', backdropFilter: 'blur(15px)', border: '1px solid', borderColor: 'success.200' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 3, color: 'success.700', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>{formState.id ? 'edit' : 'add'}</span>
            {formState.id ? 'Edit Template' : 'New Template'}
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <EnhancedInput label="Template Name" value={formState.name} onChange={(e) => setFormState({ ...formState, name: e.target.value })} placeholder="Bond Template Name" required startDecorator={<span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>list_alt</span>} />
            </Grid>
            <Grid xs={12}>
              <EnhancedInput label="Default Agency" value={formState.default_agency} onChange={(e) => setFormState({ ...formState, default_agency: e.target.value })} placeholder="State Agency" startDecorator={<span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>business</span>} />
            </Grid>
            <Grid xs={12} sm={6}>
              <Box sx={{ mb: 2 }}><Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Type</Typography><Select value={formState.record_type} onChange={(_, val) => setFormState({ ...formState, record_type: val })} sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}><Option value="Permit">Permit</Option><Option value="Bond">Bond</Option></Select></Box>
            </Grid>
            <Grid xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}><Checkbox checked={formState.is_required} onChange={(e) => setFormState({ ...formState, is_required: e.target.checked })} /><Typography level="body-sm">Required Template</Typography></Box>
            </Grid>
            <Grid xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <EnhancedButton variant="outlined" onClick={() => setOpen(false)}>Cancel</EnhancedButton>
                <EnhancedButton gradient onClick={handleSave} startDecorator={<span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>save</span>}>Save Template</EnhancedButton>
              </Box>
            </Grid>
          </Grid>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default ProjectTemplateManager;